import { Stack } from '@mui/material';
import { t } from 'i18next';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { bind } from '../../../common/utils/decorators';
import { ErrorNotification, SuccessNotification } from '../../../common/utils/notifications/Notification';
import { TestValueWrapper } from '../../../common/utils/TestValueWrapper';
import { ProjectJira } from '../../../integrations/jira/models/ProjectJira';
import { JiraRepository } from '../../../integrations/jira/services/JiraRepository';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { ACTIVE_STATUSES, NON_ACTIVE_STATUSES, TaniumStatus } from '../../../integrations/tanium/enums/TaniumStatus';
import { ProjectTanium } from '../../../integrations/tanium/models/ProjectTanium';
import { TaniumRepository } from '../../../integrations/tanium/services/TaniumRepository';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { ProjectIntegrations } from '../components/ProjectIntegrations';

type Props = WithRouterProps & {};

type State = BaseState & {
  initialLoad: boolean;
  isLoading: boolean;
  jira: {
    projectJira: ProjectJira | null;
    isJiraLinkEstablished: boolean;
    isIssueExportConfigured: boolean;
    jiraBaseUrl: string;
    isIssueCollectorConfigured: boolean;
    canEditJira: boolean;
  };
  tanium: {
    isOrgIntegrationConfigured: boolean;
    projectTanium: ProjectTanium | null;
    isTaniumActive: boolean;
    canEditTanium: boolean;
  };
};

export class Controller extends BaseController<Props, State> {
  private readonly integrationsRepository = new IntegrationsRepository();
  private readonly jiraRepository = new JiraRepository();
  private readonly modalManager = new ModalManager();
  private readonly taniumRepository = new TaniumRepository();
  private taniumFetchInterval: NodeJS.Timeout | null = null;

  private projectId: number;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ projectId: this.projectId } = this.parseNumericIds(['projectId']));

    this.state = {
      ...super.getBaseState(),
      initialLoad: true,
      isLoading: true,
      jira: {
        projectJira: null,
        isJiraLinkEstablished: false,
        isIssueExportConfigured: false,
        jiraBaseUrl: '',
        isIssueCollectorConfigured: false,
        canEditJira: false,
      },
      tanium: {
        isOrgIntegrationConfigured: false,
        projectTanium: null,
        isTaniumActive: false,
        canEditTanium: false,
      },
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.fetchProjectIntegrations();
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.stopTaniumFetch();
    this.jiraRepository.cancelAllRequests();
    this.integrationsRepository.cancelAllRequests();
  }

  async fetchProjectIntegrations(): Promise<void> {
    this.setState({ isLoading: true, initialLoad: true });
    try {
      const [integrations, projectJira, jiraPermission, projectTanium, taniumPermission] = await Promise.all([
        this.integrationsRepository.getIntegrations(),
        this.jiraRepository.getProjectJira(this.projectId),
        this.permissionsService.canUpdateProjectJira(this.projectId),
        this.taniumRepository.getProjectTanium(this.projectId),
        this.permissionsService.canUpdateProjectTanium(this.projectId),
      ]);
      const jiraExport = integrations.getJiraExport();
      const taniumIntegration = integrations.getTanium();
      this.setState((prev) => ({
        initialLoad: false,
        isLoading: false,
        jira: {
          ...prev.jira,
          projectJira: projectJira,
          isJiraLinkEstablished: !!jiraExport,
          isIssueExportConfigured: Boolean(!!projectJira?.getProjectKey() && !!projectJira.getIssueTypeKey()),
          isIssueCollectorConfigured: !!projectJira?.getIssueCollectorCode(),
          jiraBaseUrl: jiraExport?.getJiraUrl() ?? '',
          canEditJira: jiraPermission,
        },
        tanium: {
          ...prev.tanium,
          projectTanium,
          isOrgIntegrationConfigured: !!taniumIntegration,
          isTaniumActive: !!projectTanium?.getComputerGroup(),
          canEditTanium: taniumPermission,
        },
      }));
      const status = projectTanium?.getStatus();
      if (status && ACTIVE_STATUSES.includes(status)) {
        this.startTaniumFetch();
      }
    } catch (e: unknown) {
      this.setState({ initialLoad: false, isLoading: false });
      this.handleError(e, { responseError: t('projectIntegrations.fetchError') });
    }
  }

  @bind
  async onJiraSave(formCode: string, projectKey: string, issueType: string) {
    const projectJira = this.state.jira.projectJira;
    const [initialFormCode, initialProjectKey, initialIssueType] = [
      projectJira?.getIssueCollectorCode() ?? '',
      projectJira?.getProjectKey() ?? '',
      projectJira?.getIssueTypeKey() ?? '',
    ];
    if (formCode === initialFormCode && projectKey === initialProjectKey && issueType === initialIssueType) {
      return;
    }
    if (formCode !== initialFormCode) {
      this.updateProjectJiraIssueCollector(formCode);
    }
    if (projectKey !== initialProjectKey || issueType !== initialIssueType) {
      this.updateProjectJiraDetails(projectKey, issueType);
    }
  }

  private async updateProjectJiraDetails(projectKey: string, issueType: string) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.jiraRepository.checkJiraIntegration(projectKey, issueType);
      if (testConnectionResult.isSuccessful()) {
        const projectJira = await this.jiraRepository.updateProjectJiraIntegration(this.projectId, projectKey, issueType);
        this.showMessage(new SuccessNotification(t('projectIntegrations.jiraIssueExport.fieldsUpdated')));
        this.setState((prev) => ({
          isLoading: false,
          jira: { ...prev.jira, projectJira, isIssueExportConfigured: Boolean(!!projectJira?.getProjectKey() && !!projectJira.getIssueTypeKey()) },
        }));
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.jiraExport.testFail');
        this.showMessage(new ErrorNotification(message));
        this.setState({ isLoading: false });
      }
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async onTestConnectionClick(projectKey: string, issueType: string) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.jiraRepository.checkJiraIntegration(projectKey, issueType);
      if (testConnectionResult.isSuccessful()) {
        this.showMessage(new SuccessNotification(t('integrationList.jiraExport.testSuccess')));
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.venafi.testFail');
        this.showMessage(new ErrorNotification(message));
      }
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  private async updateProjectJiraIssueCollector(formCode: string) {
    try {
      this.setState({
        isLoading: true,
      });
      const projectJira = await this.jiraRepository.updateProjectJiraIssueCollector(this.projectId, formCode);
      this.showMessage(new SuccessNotification(t('projectIntegrations.jiraIssueCollector.formCodeUpdated')));
      this.setState((prev) => ({
        isLoading: false,
        jira: { ...prev.jira, projectJira, isIssueCollectorConfigured: !!projectJira?.getIssueCollectorCode() },
      }));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async onUnlinkJiraIssueCollector() {
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('projectIntegrations.jiraIssueCollector.title', null);
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.jiraRepository.unlinkProjectJiraIssueCollector(this.projectId);
      const updatedProjectJira = await this.jiraRepository.getProjectJira(this.projectId);
      this.setState((prev) => ({
        jira: { ...prev.jira, jiraFormCode: '', isIssueCollectorConfigured: false, projectJira: updatedProjectJira },
        isLoading: false,
      }));
      this.showMessage(new SuccessNotification(t('projectIntegrations.jiraIssueCollector.unlinked')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async onUnlinkJiraIntegration() {
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('projectIntegrations.jiraIssueExport.title', null);
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.jiraRepository.unlinkProjectJiraIntegration(this.projectId);
      const projectJira = await this.jiraRepository.getProjectJira(this.projectId);
      this.setState((prev) => ({
        jira: { ...prev.jira, jiraProjectKey: '', jiraIssueType: '', isIssueExportConfigured: false, projectJira },
        isLoading: false,
      }));
      this.showMessage(new SuccessNotification(t('projectIntegrations.jiraIssueExport.unlinked')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  async getProjectTanium() {
    try {
      const projectTanium = await this.taniumRepository.getProjectTanium(this.projectId);
      this.setState((prev) => ({
        tanium: {
          ...prev.tanium,
          projectTanium,
        },
      }));
      const currentStatus = projectTanium?.getStatus();
      if (currentStatus && NON_ACTIVE_STATUSES.includes(currentStatus)) {
        this.stopTaniumFetch();
      }
    } catch (e: unknown) {
      this.handleError(e);
    }
  }

  @bind
  async updateTaniumComputerGroup(computerGroup: string) {
    try {
      this.setState({ isLoading: true });
      const connectionResult = await this.taniumRepository.checkTaniumComputerGroup(computerGroup);
      const reason = connectionResult.getReason();
      if (connectionResult.isSuccessful()) {
        const projectTanium = await this.taniumRepository.updateProjectTaniumComputerGroup(this.projectId, computerGroup);
        this.setState((prev) => ({
          isLoading: false,
          tanium: {
            ...prev.tanium,
            projectTanium,
            isTaniumActive: !!projectTanium.getComputerGroup(),
          },
        }));
        this.showMessage(new SuccessNotification(reason ?? t('projectIntegrations.tanium.computerGroupUpdated')));
      } else {
        this.showMessage(new ErrorNotification(reason ?? t('somethingWentWrong')));
        this.setState({ isLoading: false });
        return;
      }
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async testComputerGroup(computerGroup: string) {
    try {
      this.setState({ isLoading: true });
      const connectionResult = await this.taniumRepository.checkTaniumComputerGroup(computerGroup);
      this.setState({ isLoading: false });
      const reason = connectionResult.getReason();
      if (connectionResult.isSuccessful() && reason) {
        this.showMessage(new SuccessNotification(reason));
      }
      if (!connectionResult.isSuccessful() && reason) {
        this.showMessage(new ErrorNotification(reason));
      }
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  stopTaniumFetch() {
    this.taniumRepository.cancelAllRequests();
    if (this.taniumFetchInterval) {
      clearInterval(this.taniumFetchInterval);
      this.taniumFetchInterval = null;
    }
  }

  startTaniumFetch() {
    this.stopTaniumFetch();
    this.taniumFetchInterval = setInterval(() => {
      this.getProjectTanium();
    }, TestValueWrapper.wrap(2500, 500));
    this.getProjectTanium();
  }

  @bind
  async startTaniumRetrieval() {
    try {
      this.setState({ isLoading: true });
      await this.taniumRepository.startTaniumRetrieval(this.projectId);
      this.setState({ isLoading: false });
      this.startTaniumFetch();
    } catch (e: unknown) {
      this.stopTaniumFetch();
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async stopTaniumRetrieval() {
    const currentStatus = this.state.tanium.projectTanium?.getStatus();
    if (currentStatus !== TaniumStatus.RUNNING) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.taniumRepository.stopTaniumRetrieval(this.projectId);
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async unlinkProjectTanium() {
    const currentStatus = this.state.tanium.projectTanium?.getStatus();
    if (currentStatus && ACTIVE_STATUSES.includes(currentStatus)) {
      return;
    }
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('projectIntegrations.tanium.title', null);
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.taniumRepository.unlinkProjectTanium(this.projectId);
      const projectTanium = await this.taniumRepository.getProjectTanium(this.projectId);
      this.setState((prev) => ({
        isLoading: false,
        tanium: {
          ...prev.tanium,
          projectTanium,
          isTaniumActive: false,
        },
      }));
      this.showMessage(new SuccessNotification(t('projectIntegrations.tanium.unlinked')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async getTaniumLogDownloadLink() {
    try {
      this.setState({ isLoading: true });
      const taniumLog = await this.taniumRepository.getProjectTaniumLog(this.projectId);
      this.setState({ isLoading: false });
      if (!taniumLog) {
        this.showMessage(new ErrorNotification(t('projectIntegrations.tanium.noLogsError')));
        return;
      }
      const url = window.URL.createObjectURL(new Blob([taniumLog], { type: 'text/plain' }));
      const completedAt = this.state.tanium.projectTanium?.getCompletedAt();
      const formattedCompletedAt = completedAt ? new Date(completedAt).toUTCString() : new Date().toUTCString();
      const fileName = `Project-${this.projectId}-Tanium-${formattedCompletedAt}.log`;
      this.download(fileName, url);
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  private download(filename: string, objectUrl: string) {
    const a = document.createElement('a');
    a.setAttribute('style', 'display: none');
    a.href = objectUrl;
    a.download = filename;

    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(objectUrl);
    a.remove();
  }

  render() {
    const { initialLoad, isLoading } = this.state;
    const { isJiraLinkEstablished, isIssueCollectorConfigured, isIssueExportConfigured, projectJira, jiraBaseUrl, canEditJira } = this.state.jira;
    const { projectTanium, isOrgIntegrationConfigured, isTaniumActive, canEditTanium } = this.state.tanium;

    return this.renderBase(
      <Stack sx={{ justifyContent: 'space-between', height: '100%' }} direction={'column'} spacing={2}>
        <ProjectIntegrations
          initialLoad={initialLoad}
          isLoading={isLoading}
          jira={{
            canUpdateJira: canEditJira,
            issueExport: {
              isJiraLinkEstablished: isJiraLinkEstablished,
              isIssueExportConfigured: isIssueExportConfigured,
              baseUrl: jiraBaseUrl,
              projectKey: projectJira?.getProjectKey() ?? '',
              issueType: projectJira?.getIssueTypeKey() ?? '',
              onUnlinkIssueExport: this.onUnlinkJiraIntegration,
              onTestIssueExport: this.onTestConnectionClick,
            },
            issueCollector: {
              isIssueCollectorConfigured: isIssueCollectorConfigured,
              formCode: projectJira?.getIssueCollectorCode() ?? '',
              onUnlinkIssueCollector: this.onUnlinkJiraIssueCollector,
            },
            handleSave: this.onJiraSave,
          }}
          tanium={{
            isOrgIntegrationConfigured: isOrgIntegrationConfigured,
            isTaniumActive: isTaniumActive,
            projectTanium: projectTanium,
            canUpdateTanium: canEditTanium,
            handleSave: this.updateTaniumComputerGroup,
            handleTestComputerGroup: this.testComputerGroup,
            handleStartTanium: this.startTaniumRetrieval,
            handleStopTanium: this.stopTaniumRetrieval,
            handleUnlinkTanium: this.unlinkProjectTanium,
            handleGetLog: this.getTaniumLogDownloadLink,
          }}
        />
      </Stack>
    );
  }
}

export const ProjectIntegrationsController = withRouter(Controller);
